import React from "react";
import "../assets/css/attendanceTrend.css";
import SkSelect from "./SkSelect";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

// Fixed series order — colour follows the category, never its rank.
const SERIES = [
  { key: "Present", label: "Present", color: "#16A34A" },
  { key: "OnDuty", label: "On Duty", color: "#2563EB" },
  { key: "Leave", label: "Leave", color: "#F59E0B" },
  { key: "Absent", label: "Absent", color: "#DC2626" },
];

// Selectable trend windows shown in the header dropdown.
export const TREND_RANGES = [
  { value: 7, label: "Last 7 days" },
  { value: 14, label: "Last 14 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

const AttendanceTrendChart = ({
  title = "Attendance Insights",
  labels = [],
  series = {},
  days = 7,
  onDaysChange,
}) => {
  const hasData = SERIES.some((s) =>
    (series[s.key] || []).some((v) => Number(v) > 0)
  );

  const data = {
    labels,
    datasets: SERIES.map((s) => ({
      label: s.label,
      data: series[s.key] || [],
      borderColor: s.color,
      backgroundColor: s.color,
      borderWidth: 2,
      tension: 0.4,
      pointRadius: 0,
      pointHoverRadius: 5,
      pointHoverBorderWidth: 2,
      pointHoverBorderColor: "#fff",
      fill: false,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false }, // custom HTML legend below the title
      tooltip: {
        backgroundColor: "#1f2937",
        titleColor: "#fff",
        bodyColor: "#fff",
        padding: 12,
        cornerRadius: 8,
        usePointStyle: true,
        boxPadding: 6,
      },
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        border: { display: false },
        ticks: {
          color: "#94a3b8",
          font: { size: 12 },
          // Longer windows would collide; thin the labels out instead.
          autoSkip: true,
          maxTicksLimit: 8,
          maxRotation: 0,
          minRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
        grid: { color: "#eef0f4", drawBorder: false, borderDash: [4, 4] },
        border: { display: false },
        ticks: {
          color: "#94a3b8",
          font: { size: 12 },
          precision: 0,
          callback: (v) => (v >= 1000 ? `${v / 1000}k` : v),
        },
      },
    },
  };

  return (
    <div className="trend-card">
      <div className="trend-header">
        <h4>{title}</h4>
        <SkSelect
          width={140}
          value={days}
          options={TREND_RANGES}
          onChange={(value) => onDaysChange?.(Number(value))}
          ariaLabel="Trend date range"
        />
      </div>

      <div className="trend-legend">
        {SERIES.map((s) => (
          <span className="trend-legend-item" key={s.key}>
            <i style={{ background: s.color }}></i>
            {s.label}
          </span>
        ))}
      </div>

      <div className="trend-body">
        <Line data={data} options={options} />
        {!hasData && (
          <div className="trend-empty">No attendance recorded in this range</div>
        )}
      </div>
    </div>
  );
};

export default AttendanceTrendChart;
